import React, { useState } from "react";
import { useSearchParams, useParams } from "react-router";
import { UserRole } from "../models/User";
import { FlaggedPathsTable } from "../components/FlaggedPaths/FlaggedPathsTable";
import { ExportFlaggedPathsToCsvStatusTable } from "../components/FlaggedPaths/ExportFlaggedPathsToCsvStatusTable";
import CreateExportFlaggedPathsToCsvTable from "../components/FlaggedPaths/CreateExportFlaggedPathsToCsvTable";
import { FlaggedPathSummary } from "../models/FlaggedPath";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface FlaggedPathsPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

const FlaggedPathsPage: React.FC<FlaggedPathsPageProps> = ({ userRole, isAssignedUser }) => {
  const { organizationId } = useParams();
  const [searchParams] = useSearchParams();
  const initialSearchTerm = searchParams.get("search") || "";
  const [newFlaggedPathsForExport, setNewFlaggedPathsForExport] = useState<FlaggedPathSummary[]>([]);
  const queuedCount = newFlaggedPathsForExport.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Flagged Paths<DocsHelpLink to="concepts/flagged-paths" /></h2>
        {userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage
            {queuedCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {queuedCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <FlaggedPathsTable
            initialSearchTerm={initialSearchTerm}
            newFlaggedPathsForExport={newFlaggedPathsForExport}
            setNewFlaggedPathsForExport={setNewFlaggedPathsForExport}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage flagged path exports</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 gap-4 p-4 overflow-auto">
              {newFlaggedPathsForExport.length > 0 && (
                <>
                  <div className="flex items-center gap-2 pt-2">
                    <Separator className="flex-1" />
                    <span className="font-bold text-sm">New</span>
                    <Separator className="flex-1" />
                  </div>
                  <CreateExportFlaggedPathsToCsvTable
                    organizationId={organizationId ?? ""}
                    newFlaggedPathsForExport={newFlaggedPathsForExport}
                    setNewFlaggedPathsForExport={setNewFlaggedPathsForExport}
                    userRole={userRole}
                    isAssignedUser={isAssignedUser}
                  />
                  <div className="flex items-center gap-2">
                    <Separator className="flex-1" />
                    <span className="font-bold text-sm">Existing exports</span>
                    <Separator className="flex-1" />
                  </div>
                </>
              )}
              <div className="flex-1">
                <ExportFlaggedPathsToCsvStatusTable
                  userRole={userRole}
                  isAssignedUser={isAssignedUser}
                  organizationId={organizationId ?? ""}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlaggedPathsPage;
